import React, { useEffect, useMemo, useRef, useState } from 'react';
import Footer from './Footer';
import { scrollElementToPageTop } from './ScrollToTop';
import {
    RSVP_CUTOFF_LABEL,
    RSVP_EVENTS,
    lookupHousehold,
    saveHouseholdRsvp,
} from '../data/rsvpApi';
import './Rsvp.css';

const DEFAULT_ATTENDANCE = RSVP_EVENTS.reduce((accumulator, event) => {
    accumulator[event.id] = '';
    return accumulator;
}, {});

const EMPTY_PLUS_ONE_RESPONSE = {
    name: '',
    attendance: { ...DEFAULT_ATTENDANCE },
    entree: 'none',
    allergies: '',
    notes: '',
};

const MAX_NOTES_LENGTH = 2000;

const getPrimaryMembers = (household) => household.members.filter((member) => !member.isPlusOneGuest);

const getLinkedPlusOneMap = (household) => household.members.reduce((accumulator, member) => {
    if (member.isPlusOneGuest && member.primaryGuestId) {
        accumulator[member.primaryGuestId] = member;
    }
    return accumulator;
}, {});

const createUiHousehold = (household) => ({
    ...household,
    members: getPrimaryMembers(household),
});

const createDefaultResponse = (member) => ({
    bringingPlusOne: false,
    plusOneGuestId: member.plusOneGuestId || null,
    plusOneName: '',
    attendance: member.invitedEvents.reduce((accumulator, eventId) => {
        accumulator[eventId] = '';
        return accumulator;
    }, {}),
    entree: 'none',
    allergies: '',
    notes: '',
    plusOneResponse: {
        ...EMPTY_PLUS_ONE_RESPONSE,
        attendance: member.invitedEvents.reduce((accumulator, eventId) => {
            accumulator[eventId] = '';
            return accumulator;
        }, {}),
    },
});

const createResponsesFromHousehold = (household) => {
    const linkedPlusOneMap = getLinkedPlusOneMap(household);

    return getPrimaryMembers(household).reduce((accumulator, member) => {
        const defaultResponse = createDefaultResponse(member);
        const plusOneMember = linkedPlusOneMap[member.id];

        accumulator[member.id] = {
            ...defaultResponse,
            bringingPlusOne: Boolean(member.bringingPlusOne || plusOneMember),
            plusOneGuestId: member.plusOneGuestId || plusOneMember?.id || null,
            plusOneName: plusOneMember?.name || '',
            attendance: {
                ...defaultResponse.attendance,
                ...(member.attendance || {}),
            },
            entree: member.entree || defaultResponse.entree,
            allergies: member.allergies || '',
            notes: member.notes || '',
            plusOneResponse: {
                ...defaultResponse.plusOneResponse,
                name: plusOneMember?.name || '',
                attendance: {
                    ...defaultResponse.plusOneResponse.attendance,
                    ...(plusOneMember?.attendance || {}),
                },
                entree: plusOneMember?.entree || defaultResponse.plusOneResponse.entree,
                allergies: plusOneMember?.allergies || '',
                notes: plusOneMember?.notes || '',
            },
        };
        return accumulator;
    }, {});
};

const getAttendanceLabel = (value) => {
    if (value === 'yes') {
        return 'Attending';
    }

    if (value === 'no') {
        return 'Not attending';
    }

    return 'No response';
};

const getEntreeLabel = (value) => {
    if (value === 'none') {
        return 'None (no dinner)';
    }

    if (value === 'veggie') {
        return 'Vegetarian';
    }

    if (value === 'beef') {
        return 'Beef (short rib)';
    }

    if (value === 'fish') {
        return 'Fish (cod)';
    }

    return value || 'None';
};

const formatNameList = (names) => {
    if (names.length === 0) {
        return 'your guests';
    }

    if (names.length === 1) {
        return names[0];
    }

    if (names.length === 2) {
        return `${names[0]} and ${names[1]}`;
    }

    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const RESPONSE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
});

const getOrdinalSuffix = (day) => {
    const remainder = day % 100;
    if (remainder >= 11 && remainder <= 13) {
        return 'th';
    }

    if (day % 10 === 1) {
        return 'st';
    }

    if (day % 10 === 2) {
        return 'nd';
    }

    if (day % 10 === 3) {
        return 'rd';
    }

    return 'th';
};

const formatResponseDate = (timestamp) => {
    if (!timestamp) {
        return '';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const parts = RESPONSE_DATE_FORMATTER.formatToParts(date).reduce((accumulator, part) => {
        accumulator[part.type] = part.value;
        return accumulator;
    }, {});
    const day = Number(parts.day);
    const dayLabel = Number.isNaN(day) ? parts.day : `${day}${getOrdinalSuffix(day)}`;
    const dayPeriod = (parts.dayPeriod || '').toLowerCase();

    return `${parts.month} ${dayLabel}, ${parts.hour}:${parts.minute}${dayPeriod} EST`;
};

const getResponseAttendance = (response, responseKey, eventId) => (
    responseKey === 'attendance'
        ? response.attendance[eventId]
        : response.plusOneResponse.attendance[eventId]
);

const hasSavedMemberResponse = (member) => (
    Object.values(member.attendance || {}).some((value) => value)
    || Boolean(member.allergies)
    || Boolean(member.notes)
    || Boolean(member.entree)
    || Boolean(member.plusOneGuestId)
    || Boolean(member.bringingPlusOne)
);

const getMemberResponseStatus = (member, lastUpdated) => {
    if (!hasSavedMemberResponse(member)) {
        return 'Awaiting reply';
    }

    const responseDate = formatResponseDate(lastUpdated);
    return responseDate ? `Last Updated ${responseDate}` : 'Responded';
};

const toGuestUpdate = (response) => {
    const receptionAttendance = response.attendance?.reception;
    return {
        bringingPlusOne: Boolean(response.bringingPlusOne),
        plusOneGuestId: response.plusOneGuestId || null,
        attendance: { ...(response.attendance || {}) },
        entree: receptionAttendance === 'yes' ? response.entree : '',
        allergies: receptionAttendance === 'yes' ? response.allergies : '',
        notes: response.notes || '',
    };
};

const toPlusOneUpdate = (response) => {
    const receptionAttendance = response.plusOneResponse.attendance?.reception;
    const plusOneUpdate = {
        attendance: { ...(response.plusOneResponse.attendance || {}) },
        entree: receptionAttendance === 'yes' ? response.plusOneResponse.entree : '',
        allergies: receptionAttendance === 'yes' ? response.plusOneResponse.allergies : '',
        notes: response.plusOneResponse.notes || '',
    };

    if (response.plusOneName.trim()) {
        plusOneUpdate.name = response.plusOneName.trim();
    }

    return plusOneUpdate;
};

const toNewPlusOnePayload = (response) => {
    const receptionAttendance = response.plusOneResponse.attendance?.reception;
    return {
        name: response.plusOneName.trim(),
        attendance: { ...(response.plusOneResponse.attendance || {}) },
        entree: receptionAttendance === 'yes' ? response.plusOneResponse.entree : '',
        allergies: receptionAttendance === 'yes' ? response.plusOneResponse.allergies : '',
        notes: response.plusOneResponse.notes || '',
    };
};

const buildSavePayload = (selectedMembers, responses) => {
    const selectedMemberIds = [...selectedMembers];
    const payloadResponses = {};

    selectedMembers.forEach((memberId) => {
        const response = responses[memberId];
        if (!response) {
            return;
        }

        const primaryUpdate = toGuestUpdate(response);

        if (response.bringingPlusOne) {
            if (response.plusOneGuestId) {
                primaryUpdate.plusOneGuestId = response.plusOneGuestId;
                if (!selectedMemberIds.includes(response.plusOneGuestId)) {
                    selectedMemberIds.push(response.plusOneGuestId);
                }
                payloadResponses[response.plusOneGuestId] = toPlusOneUpdate(response);
            } else {
                primaryUpdate.plusOneGuest = toNewPlusOnePayload(response);
            }
        } else {
            primaryUpdate.plusOneGuestId = null;
        }

        payloadResponses[memberId] = primaryUpdate;
    });

    return {
        selectedMemberIds,
        responses: payloadResponses,
    };
};

const findMissingPlusOneNameMember = (selectedMembers, responses, householdMembers) => {
    for (const memberId of selectedMembers) {
        const response = responses[memberId];
        if (!response?.bringingPlusOne || response.plusOneName.trim()) {
            continue;
        }

        const member = householdMembers.find((candidate) => candidate.id === memberId);
        return member || { id: memberId, name: 'this guest' };
    }

    return null;
};

function StepHeading({ step, title, action }) {
    return (
        <div className="panel-heading">
            <div>
                <span className="section-subtitle">{step}</span>
                <h2 className="section-title">{title}</h2>
            </div>
            {action}
        </div>
    );
}

function Rsvp() {
    const [lookupValue, setLookupValue] = useState('');
    const [household, setHousehold] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [responses, setResponses] = useState({});
    const [activeMemberIndex, setActiveMemberIndex] = useState(0);
    const [view, setView] = useState('lookup');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const responseWindowRef = useRef(null);
    const previousPageRef = useRef({ view, activeMemberIndex });

    const activeMembers = useMemo(() => {
        if (!household) {
            return [];
        }

        return household.members.filter((member) => selectedMembers.includes(member.id));
    }, [household, selectedMembers]);

    const activeMember = activeMembers[activeMemberIndex] || null;

    useEffect(() => {
        const previousPage = previousPageRef.current;
        const pageChanged = previousPage.view !== view
            || previousPage.activeMemberIndex !== activeMemberIndex;

        previousPageRef.current = { view, activeMemberIndex };
        if (pageChanged) {
            scrollElementToPageTop(responseWindowRef.current);
        }
    }, [view, activeMemberIndex]);

    const resetWorkflow = () => {
        setHousehold(null);
        setSelectedMembers([]);
        setResponses({});
        setActiveMemberIndex(0);
        setView('lookup');
        setLoading(false);
        setSaving(false);
        setError('');
    };

    const handleLookup = async (event) => {
        event.preventDefault();

        if (!lookupValue.trim()) {
            setError('Enter your full name to continue.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const result = await lookupHousehold(lookupValue);

            if (!result) {
                setHousehold(null);
                setSelectedMembers([]);
                setResponses({});
                setView('lookup');
                setError('We could not find a matching invitation. Please double-check the name entered.');
                return;
            }

            setHousehold(createUiHousehold(result));
            setSelectedMembers(getPrimaryMembers(result).map((member) => member.id));
            setResponses(createResponsesFromHousehold(result));
            setActiveMemberIndex(0);
            setView('household');
        } catch (lookupError) {
            if (lookupError?.status === 404) {
                setError('No invitation found. Please double-check the name entered');
            } else {
                const message = lookupError?.message || 'Unknown error.';
                setError(`We could not look up your invitation just now. ${message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMemberSelection = (memberId) => {
        const nextSelected = selectedMembers.includes(memberId)
            ? selectedMembers.filter((id) => id !== memberId)
            : [...selectedMembers, memberId];

        setSelectedMembers(nextSelected);

        if (nextSelected.length === 0) {
            setActiveMemberIndex(0);
            return;
        }

        const nextActiveIndex = Math.min(activeMemberIndex, nextSelected.length - 1);
        setActiveMemberIndex(nextActiveIndex);
    };

    const handleStartResponses = () => {
        if (selectedMembers.length === 0) {
            setError('Select at least one household member to continue.');
            return;
        }

        setError('');
        setActiveMemberIndex(0);
        setView('details');
    };

    const updateMemberResponse = (memberId, updater) => {
        setResponses((currentResponses) => ({
            ...currentResponses,
            [memberId]: updater(currentResponses[memberId]),
        }));
    };

    const updateAttendance = (memberId, responseKey, eventId, value) => {
        updateMemberResponse(memberId, (currentResponse) => {
            if (responseKey === 'attendance') {
                return {
                    ...currentResponse,
                    attendance: {
                        ...currentResponse.attendance,
                        [eventId]: value,
                    },
                };
            }

            return {
                ...currentResponse,
                plusOneResponse: {
                    ...currentResponse.plusOneResponse,
                    attendance: {
                        ...currentResponse.plusOneResponse.attendance,
                        [eventId]: value,
                    },
                },
            };
        });
    };

    const updateDinnerDetail = (memberId, responseKey, field, value) => {
        updateMemberResponse(memberId, (currentResponse) => {
            if (responseKey === 'attendance') {
                return {
                    ...currentResponse,
                    [field]: value,
                };
            }

            return {
                ...currentResponse,
                plusOneResponse: {
                    ...currentResponse.plusOneResponse,
                    [field]: value,
                },
            };
        });
    };

    const updatePlusOneChoice = (memberId, bringingPlusOne) => {
        updateMemberResponse(memberId, (currentResponse) => ({
            ...currentResponse,
            bringingPlusOne,
            ...(bringingPlusOne
                ? {}
                : {
                    plusOneGuestId: null,
                    plusOneName: '',
                    plusOneResponse: {
                        ...currentResponse.plusOneResponse,
                        name: '',
                        allergies: '',
                        notes: '',
                    },
                }),
        }));
    };

    const updatePlusOneName = (memberId, name) => {
        updateMemberResponse(memberId, (currentResponse) => ({
            ...currentResponse,
            plusOneName: name,
            plusOneResponse: {
                ...currentResponse.plusOneResponse,
                name,
            },
        }));
    };

    const editMemberFromReview = (memberId) => {
        setActiveMemberIndex(activeMembers.findIndex((active) => active.id === memberId));
        setView('details');
    };

    const handleSave = async () => {
        if (!household) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            const missingPlusOneMember = findMissingPlusOneNameMember(selectedMembers, responses, household.members);
            if (missingPlusOneMember) {
                setError(`Enter a plus-one name for ${missingPlusOneMember.name}, or choose "No plus-one" before saving.`);
                const missingIndex = activeMembers.findIndex((member) => member.id === missingPlusOneMember.id);
                if (missingIndex >= 0) {
                    setActiveMemberIndex(missingIndex);
                    setView('details');
                }
                return;
            }

            const payload = buildSavePayload(selectedMembers, responses);
            const savedHousehold = await saveHouseholdRsvp(
                household.householdId,
                payload.selectedMemberIds,
                payload.responses,
            );
            const savedGuestNames = savedHousehold.members
                .filter((member) => payload.selectedMemberIds.includes(member.id))
                .map((member) => member.name);
            setSuccessMessage(`RSVP saved for ${formatNameList(savedGuestNames)}. You can return any time before ${RSVP_CUTOFF_LABEL} to update it.`);
            setLookupValue('');
            resetWorkflow();
        } catch (saveError) {
            setError('We could not save your RSVP just now. Please check your responses and try again in a moment.');
        } finally {
            setSaving(false);
        }
    };

    const renderLookup = () => (
        <section className="rsvp-panel rsvp-lookup-panel">
            <div className="rsvp-panel-copy">
                <span className="section-subtitle">Step 1</span>
                <h2 className="section-title">Find Your Invitation</h2>
                <p>
                    Enter your first and last name to find your invitation. If you have already replied,
                    you can edit your response here.
                </p>
                <ul className="rsvp-notes">
                    <li>
                        Please RSVP by {RSVP_CUTOFF_LABEL}.
                    </li>
                    <li>
                        You can respond for all invited guests in your household at once, including plus-ones.
                    </li>
                </ul>
            </div>

            <form className="lookup-form" onSubmit={handleLookup}>
                <label className="field-label" htmlFor="guestLookup">Guest name</label>
                <input
                    id="guestLookup"
                    className="text-input"
                    type="text"
                    value={lookupValue}
                    onChange={(event) => setLookupValue(event.target.value)}
                    placeholder="Example: Sofia Rivera"
                    autoComplete="name"
                />
                <button className="primary-button" type="submit" disabled={loading}>
                    {loading ? 'Finding your invitation...' : 'Begin RSVP'}
                </button>
            </form>
        </section>
    );

    const renderHousehold = () => (
        <section className="rsvp-panel">
            <StepHeading
                step="Step 2"
                title="Select Guests"
            />

            <div className="household-summary">
                <div className={`edit-badge ${household.canEdit ? 'editable' : 'locked'}`}>
                    {household.canEdit ? `Editable until ${RSVP_CUTOFF_LABEL}` : 'Responses are locked'}
                </div>
            </div>

            <div className="member-selection-list">
                {household.members.map((member) => (
                    <label className="member-selection-item" key={member.id}>
                        <div className="member-selection-top">
                            <input
                                type="checkbox"
                                checked={selectedMembers.includes(member.id)}
                                onChange={() => handleMemberSelection(member.id)}
                                disabled={!household.canEdit}
                            />
                            <div>
                                <h3>
                                    {member.name}
                                    {member.plusOneAllowed && (
                                        <span className="member-plus-one"> and plus one</span>
                                    )}
                                </h3>
                            </div>
                        </div>
                        <div className="member-selection-meta">
                            <span>{getMemberResponseStatus(member, household.lastUpdated)}</span>
                        </div>
                    </label>
                ))}
            </div>

            <div className="panel-actions">
                <button className="secondary-button" type="button" onClick={() => setView('lookup')}>
                    Back
                </button>
                <button
                    className="primary-button"
                    type="button"
                    onClick={handleStartResponses}
                    disabled={!household.canEdit}
                >
                    Continue
                </button>
            </div>
        </section>
    );

    const renderAttendanceOption = (member, response, responseKey, event, value, label) => (
        <label className={`attendance-option attendance-option-${value}`}>
            <input
                type="radio"
                name={`${member.id}-${responseKey}-${event.id}`}
                checked={getResponseAttendance(response, responseKey, event.id) === value}
                onChange={() => updateAttendance(member.id, responseKey, event.id, value)}
            />
            <span>{label}</span>
        </label>
    );

    const renderEventAttendance = (member, response, responseKey = 'attendance') => (
        <div className="event-grid">
            {RSVP_EVENTS.filter((event) => member.invitedEvents.includes(event.id)).map((event) => (
                <div className="event-response-row" key={`${responseKey}-${event.id}`}>
                    <div className="event-response-main">
                        <div className="event-copy">
                            <h4>{event.label}</h4>
                            <p>{event.description}</p>
                        </div>
                        <div className="segmented-control">
                            {renderAttendanceOption(member, response, responseKey, event, 'yes', 'Yes')}
                            {renderAttendanceOption(member, response, responseKey, event, 'no', 'No')}
                        </div>
                    </div>

                    {event.id === 'reception' && (
                        getResponseAttendance(response, responseKey, event.id) === 'yes'
                    ) && (
                            <div className="event-follow-up">
                                <p className="event-follow-up-label">
                                    Dinner details
                                </p>
                                <div className="form-grid">
                                    <div className="field-group">
                                        <label className="field-label" htmlFor={`${member.id}-${responseKey}-entree`}>
                                            Entree
                                        </label>
                                        <select
                                            id={`${member.id}-${responseKey}-entree`}
                                            className="select-input"
                                            value={responseKey === 'attendance' ? response.entree : response.plusOneResponse.entree}
                                            onChange={(eventChange) => updateDinnerDetail(member.id, responseKey, 'entree', eventChange.target.value)}
                                        >
                                            <option value="none">None (no dinner)</option>
                                            <option value="beef">Beef (short rib)</option>
                                            <option value="fish">Fish (cod)</option>
                                            <option value="veggie">Vegetarian</option>
                                        </select>
                                    </div>

                                    <div className="field-group">
                                        <label className="field-label" htmlFor={`${member.id}-${responseKey}-allergies`}>
                                            Allergies or dietary notes
                                        </label>
                                        <textarea
                                            id={`${member.id}-${responseKey}-allergies`}
                                            className="textarea-input"
                                            value={responseKey === 'attendance' ? response.allergies : response.plusOneResponse.allergies}
                                            onChange={(eventChange) => updateDinnerDetail(member.id, responseKey, 'allergies', eventChange.target.value)}
                                            placeholder="Optional"
                                            rows="4"
                                        />
                                    </div>

                                </div>
                            </div>
                        )}
                </div>
            ))}
        </div>
    );

    const renderGeneralNotes = (member, response, responseKey = 'attendance') => (
        <div className="field-group general-notes-field">
            <label className="field-label" htmlFor={`${member.id}-${responseKey}-notes`}>
                Anything else we should know?
            </label>
            <textarea
                id={`${member.id}-${responseKey}-notes`}
                className="textarea-input"
                value={responseKey === 'attendance' ? response.notes : response.plusOneResponse.notes}
                onChange={(eventChange) => updateDinnerDetail(member.id, responseKey, 'notes', eventChange.target.value)}
                maxLength={MAX_NOTES_LENGTH}
                placeholder="Optional"
                rows="4"
            />
        </div>
    );

    const renderDetails = () => {
        if (!activeMember) {
            return null;
        }

        const response = responses[activeMember.id];
        const hasPlusOne = activeMember.plusOneAllowed && response.bringingPlusOne;

        return (
            <section className="rsvp-panel">
                <StepHeading
                    step="Step 3"
                    title="Guest Details"
                    action={(
                        <button className="ghost-button" type="button" onClick={() => setView('review')}>
                            Review responses
                        </button>
                    )}
                />

                <div className="guest-form">
                    <div className="guest-focus">
                        <select
                            id="activeGuest"
                            className="guest-select"
                            aria-label="Change guest"
                            value={activeMember.id}
                            onChange={(event) => {
                                const nextIndex = activeMembers.findIndex((member) => member.id === event.target.value);
                                setActiveMemberIndex(Math.max(nextIndex, 0));
                            }}
                        >
                            {activeMembers.map((member) => (
                                <option key={member.id} value={member.id}>
                                    {member.name}
                                </option>
                            ))}
                        </select>
                    </div>


                    <div className="field-group">
                        {renderEventAttendance(activeMember, response)}
                    </div>

                    {renderGeneralNotes(activeMember, response)}

                    {activeMember.plusOneAllowed && (
                        <div className="field-group">
                            <div className="field-header">
                                <h4>Bringing a plus-one?</h4>
                                <p>Select yes to enter details for your additional guest.</p>
                            </div>
                            <div className="choice-row">
                                <button
                                    type="button"
                                    className={`choice-button ${response.bringingPlusOne ? 'active' : ''}`}
                                    onClick={() => updatePlusOneChoice(activeMember.id, true)}
                                >
                                    Yes, adding a guest
                                </button>
                                <button
                                    type="button"
                                    className={`choice-button ${!response.bringingPlusOne ? 'active' : ''}`}
                                    onClick={() => updatePlusOneChoice(activeMember.id, false)}
                                >
                                    No plus-one
                                </button>
                            </div>
                        </div>
                    )}

                    {hasPlusOne && (
                        <div className="plus-one-section">
                            <div className="field-header">
                                <h4>Plus-one details</h4>
                                <p>Enter your guest's name and response details.</p>
                            </div>

                            <div className="field-group">
                                <label className="field-label" htmlFor={`${activeMember.id}-plus-one`}>
                                    Plus-one name
                                </label>
                                <input
                                    id={`${activeMember.id}-plus-one`}
                                    className="text-input"
                                    type="text"
                                    value={response.plusOneName}
                                    onChange={(event) => updatePlusOneName(activeMember.id, event.target.value)}
                                    placeholder="Enter guest name"
                                />
                            </div>

                            {renderEventAttendance(activeMember, response, 'plusOneResponse')}
                            {renderGeneralNotes(activeMember, response, 'plusOneResponse')}
                        </div>
                    )}
                </div>

                <div className="panel-actions">
                    <button
                        className="secondary-button"
                        type="button"
                        onClick={() => {
                            if (activeMemberIndex > 0) {
                                setActiveMemberIndex(activeMemberIndex - 1);
                                return;
                            }
                            setView('household');
                        }}
                    >
                        {activeMemberIndex > 0 ? 'Previous guest' : 'Back'}
                    </button>
                    {activeMemberIndex < activeMembers.length - 1 && (
                        <button
                            className="primary-button"
                            type="button"
                            onClick={() => setActiveMemberIndex(activeMemberIndex + 1)}
                        >
                            Next guest
                        </button>
                    )}
                    {activeMemberIndex === activeMembers.length - 1 && (
                        <button
                            className="primary-button"
                            type="button"
                            onClick={() => setView('review')}
                        >
                            Review responses
                        </button>
                    )}
                </div>
            </section>
        );
    };

    const renderReview = () => (
        <section className="rsvp-panel">
            <StepHeading
                step="Step 4"
                title="Review & Send"
                action={<button className="ghost-button" type="button" onClick={() => setView('details')}>Edit responses</button>}
            />

            <div className="review-list">
                {activeMembers.map((member) => {
                    const response = responses[member.id];
                    const hasPlusOne = member.plusOneAllowed && response.bringingPlusOne;

                    return (
                        <article className="review-entry" key={member.id}>
                            <div className="review-entry-header">
                                <div>
                                    <h3>{member.name}</h3>
                                </div>
                                <button className="inline-button" type="button" onClick={() => editMemberFromReview(member.id)}>
                                    Edit
                                </button>
                            </div>

                            <div className="review-events">
                                {member.invitedEvents.map((eventId) => {
                                    const eventInfo = RSVP_EVENTS.find((event) => event.id === eventId);
                                    const isReceptionEvent = eventId === 'reception';
                                    return (
                                        <div key={`${member.id}-${eventId}`}>
                                            <span>{eventInfo.label}</span>
                                            {isReceptionEvent && response.attendance[eventId] === 'yes' && (
                                                <p className="review-event-note">
                                                    Meal: {getEntreeLabel(response.entree)} • Dietary notes: {response.allergies || 'None'}
                                                </p>
                                            )}
                                            <strong>{getAttendanceLabel(response.attendance[eventId])}</strong>
                                        </div>
                                    );
                                })}
                            </div>
                            {response.notes && (
                                <><br /><p className="review-event-note">
                                    Notes: {response.notes}
                                </p></>
                            )}

                            {hasPlusOne && (
                                <div className="plus-one-review">
                                    <div>
                                        <h4>Plus-one: {response.plusOneName}</h4>
                                    </div>
                                    <div className="review-events">
                                        {member.invitedEvents.map((eventId) => {
                                            const eventInfo = RSVP_EVENTS.find((event) => event.id === eventId);
                                            const isReceptionEvent = eventId === 'reception';
                                            return (
                                                <div key={`${member.id}-plus-one-${eventId}`}>
                                                    <span>{eventInfo.label}</span>
                                                    {isReceptionEvent && response.plusOneResponse.attendance[eventId] === 'yes' && (
                                                        <p className="review-event-note">
                                                            Meal: {getEntreeLabel(response.plusOneResponse.entree)} • Dietary notes: {response.plusOneResponse.allergies || 'None'}
                                                        </p>
                                                    )}
                                                    <strong>{getAttendanceLabel(response.plusOneResponse.attendance[eventId])}</strong>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {response.plusOneResponse.notes && (
                                        <p className="review-event-note">
                                            Notes: {response.plusOneResponse.notes}
                                        </p>
                                    )}
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>

            <div className="panel-actions">
                <button className="secondary-button" type="button" onClick={() => setView('details')}>
                    Back
                </button>
                <button className="primary-button" type="button" onClick={handleSave} disabled={saving}>
                    {saving ? 'Sending RSVP...' : 'Send RSVP'}
                </button>
            </div>
        </section>
    );

    return (
        <div className="rsvp-page">
            <header className="page-header">
                <br /><br />
                <div className="page-header-image-layer" aria-hidden="true"></div>
                <h1>RSVP</h1>
                <p></p><br />
            </header>

            <div className="botanical-wrapper">
                <div className="botanical-left"></div>
                <div className="botanical-right"></div>

                <section className="rsvp-intro">
                    <div className="section-header">
                        <h2 className="section-title">Celebrate With Us</h2>
                        <span className="section-subtitle">We request your response by {RSVP_CUTOFF_LABEL}</span>
                    </div>
                    <p>
                        We are excited to gather with you in the Berkshires! You can respond for everyone
                        in your household together, then review the details before submitting your response.
                    </p>
                </section>

                <div ref={responseWindowRef}>
                    {error && <div className="status-message error">{error}</div>}
                    {successMessage && <div className="status-message success">{successMessage}</div>}

                    {view === 'lookup' && renderLookup()}
                    {view === 'household' && household && renderHousehold()}
                    {view === 'details' && household && renderDetails()}
                    {view === 'review' && household && renderReview()}
                </div>
            </div>

            <Footer />
        </div>
    );
}

export default Rsvp;
